// The home page hero's field (Hero.jsx): a full-bleed honeycomb that lights up under the
// pointer, and the app's own bees (Bee.jsx) that fly from the queen in the headline to
// cells, fill them with honey while they work, and end done or waiting for your OK.
//
// Everything moves on one requestAnimationFrame loop with the field's own clock, so it
// stops while the hero is off screen or the tab is hidden and goes on where it was. The
// copy comes in as children and floats over the field: only its links and buttons take
// clicks, so every cell, also under the words, lights up and takes a click. People who
// asked for less motion get a still honeycomb with a few bees in their end states.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Bee from '../bees/Bee';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { busyLimit, cellRadius, freeCells, layoutCells, markFree, nearestCell, unionBox } from './hiveGrid';
import './hive.css';

// The app's colors (tailwind.config.js) as canvas rgb: honey, a lighter honey for the
// rim of a full cell, honey ink for its check, and attention for "needs you".
const HONEY = '245,184,61';
const HONEY_LIGHT = '255,214,120';
const INK = '26,18,3';
const ATTENTION = '255,139,74';

// How long things take, in ms of the field's clock.
const OUT = [1300, 500]; // the flight from the queen to a cell: at least, plus up to
const WORK = [3000, 2600]; // filling the cell with honey
const WAIT = 11000; // nobody approves: the demo does, so the hive keeps moving
const DONE = 1300; // the bee shows it is done, then flies home
const HOME = 1200;
const LINGER = 2400; // the pill stays after the bee left, then fades
const FADE = 400;
const SPAWN = 1400;
const GLOW = 9000; // a finished cell stays honey, then fades in GLOW_FADE
const GLOW_FADE = 3000;
const MAX_JOBS = 14; // clicks hand out work past the busy limit, up to this many
const STILL_MAX = 10; // with less motion bees stay: past this the oldest done one leaves

// Each engine numbers its jobs apart from the one before, so React never takes a new
// bee for an old one when the field starts over (less motion switched on or off).
let generation = 0;

const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/**
 * The field's engine: cells, jobs and the loop, outside React. It tells React only what
 * changes a few times per job (which bees and pills show, in what state) through
 * setViews, and moves the bees itself every frame through their elements (bind).
 */
function createHive({ host, canvas, ctx, still, latest, copyRef, homeRef, setViews, setFieldWidth }) {
  generation += 1;
  const gen = generation;
  const S = {
    W: 0, H: 0, R: 0, cells: [], jobs: [], seq: 0, next: 0,
    clock: 0, last: 0, raf: 0, nextSpawn: 0, spawned: 0, seeded: false, dead: false,
    onScreen: true, hidden: document.hidden,
    mouse: { x: -1e4, y: -1e4 }, counts: { work: 0, done: 0, needs: 0 }, bees: new Map(),
  };

  const emit = () => latest.current.onCounts?.({ ...S.counts });
  const patch = (id, change) => setViews((vs) => vs.map((v) => (v.id === id ? { ...v, ...change } : v)));
  const drop = (j) => {
    S.jobs = S.jobs.filter((x) => x !== j);
    setViews((vs) => vs.filter((v) => v.id !== j.id));
  };

  const center = (el) => {
    const a = el.getBoundingClientRect();
    const b = host.getBoundingClientRect();
    return { x: a.left - b.left + a.width / 2, y: a.top - b.top + a.height / 2 };
  };
  const home = () => (homeRef.current ? center(homeRef.current) : { x: S.W / 2, y: 0 });
  const perch = (cell) => ({ x: cell.x, y: cell.y - S.R * 0.12 });
  // A job's pill sits under its cell, or above it at the bottom of the field.
  const pillPlace = (cell) => {
    const below = cell.y + S.R * 0.95 + 48 < S.H;
    return { x: cell.x, y: below ? cell.y + S.R * 0.95 : cell.y - S.R * 0.95, above: !below };
  };

  const put = (j, x, y, tilt = 0) => {
    j.pos = { x, y, tilt };
    const el = S.bees.get(j.id);
    if (el) el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotate(${tilt.toFixed(1)}deg)`;
  };
  // A flight on a curve over the honeycomb, with a little wobble, tilted the way it flies.
  const flight = (from, to) => ({
    from, to, c: { x: (from.x + to.x) / 2 + (Math.random() - 0.5) * 160, y: Math.min(from.y, to.y) - 70 - Math.random() * 70 },
  });
  const fly = (j, k) => {
    const { from: a, to: b, c } = j.path;
    const e = ease(clamp(k, 0, 1));
    const u = 1 - e;
    const x = u * u * a.x + 2 * u * e * c.x + e * e * b.x;
    const y = u * u * a.y + 2 * u * e * c.y + e * e * b.y + Math.sin(S.clock / 90) * 1.4;
    const vx = 2 * u * (c.x - a.x) + 2 * e * (b.x - c.x);
    put(j, x, y, clamp(vx / 40, -16, 16));
  };
  const hover = (j) => {
    const p = perch(j.cell);
    put(j, p.x + Math.sin(S.clock / 180) * 3, p.y + Math.cos(S.clock / 230) * 2);
  };

  // ---- jobs ----
  const begin = (cell) => {
    const list = latest.current.jobs;
    if (!list.length) return;
    const spec = S.next % list.length;
    S.next += 1;
    const from = home();
    const j = {
      id: `${gen}:${++S.seq}`, cell, spec, asks: !!list[spec].asks, phase: 'out',
      t0: S.clock, dur: OUT[0] + Math.random() * OUT[1], path: flight(from, perch(cell)), pos: { x: from.x, y: from.y, tilt: 0 },
    };
    cell.state = 'work';
    cell.p = 0;
    S.jobs.push(j);
    S.counts.work += 1;
    emit();
    setViews((vs) => [...vs, { id: j.id, spec, bee: 'work', pill: 'work', shown: false, ...pillPlace(cell) }]);
  };
  const settle = (j, pill) => {
    j.phase = 'done';
    j.t0 = S.clock;
    j.cell.state = 'done';
    j.cell.born = S.clock;
    S.counts.done += 1;
    patch(j.id, { bee: 'done', pill });
  };
  const finishWork = (j) => {
    S.counts.work -= 1;
    if (j.asks) {
      j.phase = 'needs';
      j.t0 = S.clock;
      j.cell.state = 'needs';
      S.counts.needs += 1;
      patch(j.id, { bee: 'needs', pill: 'needs' });
    } else {
      settle(j, 'done');
    }
    emit();
  };
  const approve = (j) => {
    if (!j || j.phase !== 'needs') return;
    S.counts.needs -= 1;
    settle(j, 'approved');
    emit();
    if (still) requestDraw();
  };
  const spawn = () => {
    if (S.jobs.filter((j) => j.phase === 'out' || j.phase === 'work').length >= busyLimit(S.W)) return;
    if (!markCopy()) return;
    const list = freeCells(S.cells, S.jobs.map((j) => j.cell), S.R);
    if (list.length) begin(list[Math.floor(Math.random() * list.length)]);
  };

  // With less motion: a bee sits in its cell already at its end, done or asking.
  const placeStill = (cell) => {
    const list = latest.current.jobs;
    if (!list.length) return;
    const spec = S.next % list.length;
    S.next += 1;
    const p = perch(cell);
    const j = { id: `${gen}:${++S.seq}`, cell, spec, asks: !!list[spec].asks, t0: 0, pos: { x: p.x, y: p.y, tilt: 0 } };
    j.phase = j.asks ? 'needs' : 'done';
    cell.state = j.phase;
    cell.born = 0;
    S.counts[j.phase] += 1;
    S.jobs.push(j);
    setViews((vs) => [...vs, { id: j.id, spec, bee: j.phase, pill: j.phase, shown: true, ...pillPlace(cell) }]);
  };
  const seed = () => {
    if (!markCopy()) return;
    S.seeded = true;
    const want = S.W < 640 ? 3 : 5;
    const taken = [];
    for (let i = 0; i < want; i += 1) {
      const list = freeCells(S.cells, taken, S.R);
      if (!list.length) break;
      const cell = list[Math.floor(Math.random() * list.length)];
      taken.push(cell);
      placeStill(cell);
    }
    emit();
  };

  // ---- one frame ----
  const step = () => {
    const now = S.clock;
    [...S.jobs].forEach((j) => {
      const k = (now - j.t0) / (j.dur || 1);
      if (j.phase === 'out') {
        fly(j, k);
        if (k >= 1) {
          j.phase = 'work';
          j.t0 = now;
          j.dur = WORK[0] + Math.random() * WORK[1];
          patch(j.id, { shown: true });
        }
      } else if (j.phase === 'work') {
        j.cell.p = Math.min(1, k);
        hover(j);
        if (k >= 1) finishWork(j);
      } else if (j.phase === 'needs') {
        hover(j);
        // While the pointer or the focus is on its pill, it waits.
        if (j.held) j.t0 = Math.max(j.t0, now - WAIT + 3000);
        if (now - j.t0 >= WAIT) approve(j);
      } else if (j.phase === 'done') {
        hover(j);
        if (now - j.t0 >= DONE) {
          j.phase = 'home';
          j.t0 = now;
          j.dur = HOME;
          j.path = flight({ x: j.pos.x, y: j.pos.y }, home());
          patch(j.id, { bee: 'idle' });
        }
      } else if (j.phase === 'home') {
        fly(j, k);
        if (k >= 1) {
          j.phase = 'linger';
          j.t0 = now;
          patch(j.id, { bee: null });
        }
      } else if (j.phase === 'linger') {
        if (!j.fading && now - j.t0 >= LINGER) {
          j.fading = true;
          patch(j.id, { shown: false });
        }
        if (now - j.t0 >= LINGER + FADE) drop(j);
      }
    });
    if (S.cells.length && now >= S.nextSpawn) {
      spawn();
      S.nextSpawn = now + (S.spawned ? SPAWN : SPAWN / 2);
      S.spawned += 1;
    }
  };

  const hex = (x, y, r) => {
    ctx.beginPath();
    for (let i = 0; i < 6; i += 1) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      const px = x + r * Math.cos(a);
      const py = y + r * Math.sin(a);
      if (i) ctx.lineTo(px, py);
      else ctx.moveTo(px, py);
    }
    ctx.closePath();
  };
  const draw = () => {
    const { W, H, R, clock, mouse } = S;
    ctx.clearRect(0, 0, W, H);
    const pulse = still ? 0.6 : (Math.sin(clock / 260) + 1) / 2;
    const r = R - 2.5;
    S.cells.forEach((c) => {
      if (c.state === 'done' && !still && clock - c.born > GLOW + GLOW_FADE) c.state = 'empty';
      const g = Math.max(0, 1 - Math.hypot(c.x - mouse.x, c.y - mouse.y) / 190);
      c.glow = still ? g : c.glow + (g - c.glow) * 0.15;
      hex(c.x, c.y, r);
      if (c.state === 'work') {
        ctx.save();
        ctx.clip();
        ctx.fillStyle = `rgba(${HONEY},0.07)`;
        ctx.fillRect(c.x - R, c.y - R, 2 * R, 2 * R);
        const h = 2 * R * c.p;
        const wave = still ? 0 : Math.sin(clock / 320 + c.x * 0.05) * 2.2;
        ctx.fillStyle = `rgba(${HONEY},0.45)`;
        ctx.fillRect(c.x - R, c.y + R - h + wave, 2 * R, h + 4);
        ctx.restore();
        hex(c.x, c.y, r);
        ctx.strokeStyle = `rgba(${HONEY},0.75)`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      } else if (c.state === 'done') {
        const age = clock - c.born;
        const a = still || age < GLOW ? 1 : Math.max(0, 1 - (age - GLOW) / GLOW_FADE);
        ctx.fillStyle = `rgba(${HONEY},${0.52 * a})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(${HONEY_LIGHT},${0.85 * a})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.strokeStyle = `rgba(${INK},${0.9 * a})`;
        ctx.lineWidth = R < 30 ? 2.2 : 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(c.x - R * 0.28, c.y + R * 0.02);
        ctx.lineTo(c.x - R * 0.06, c.y + R * 0.24);
        ctx.lineTo(c.x + R * 0.32, c.y - R * 0.22);
        ctx.stroke();
      } else if (c.state === 'needs') {
        ctx.fillStyle = `rgba(${ATTENTION},${0.1 + 0.14 * pulse})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(${ATTENTION},${0.55 + 0.4 * pulse})`;
        ctx.lineWidth = 2;
        ctx.stroke();
      } else {
        ctx.strokeStyle = `rgba(255,255,255,${0.05 + c.glow * 0.24})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        if (c.glow > 0.02) {
          ctx.fillStyle = `rgba(${HONEY},${c.glow * 0.1})`;
          ctx.fill();
        }
      }
    });
  };

  // ---- the loop: runs only while the hero is on screen and the tab is visible ----
  const frame = (ts) => {
    const dt = S.last ? Math.min(50, ts - S.last) : 0;
    S.last = ts;
    S.clock += dt;
    step();
    draw();
    S.raf = requestAnimationFrame(frame);
  };
  // With less motion nothing loops: the field is drawn again only when something changed.
  // The still bees take their cells in the first frame, once the page has its direction.
  const requestDraw = () => {
    if (S.raf || S.dead) return;
    S.raf = requestAnimationFrame(() => {
      S.raf = 0;
      if (still && !S.seeded && S.cells.length) seed();
      draw();
    });
  };
  const sync = () => {
    if (S.dead) return;
    if (still) {
      requestDraw();
      return;
    }
    const run = S.onScreen && !S.hidden;
    if (run && !S.raf) {
      S.last = 0;
      S.raf = requestAnimationFrame(frame);
    } else if (!run && S.raf) {
      cancelAnimationFrame(S.raf);
      S.raf = 0;
    }
  };

  // ---- layout ----
  // Where the words are now, in field coordinates: every visible child of the copy (kicker,
  // headline, subtitle, buttons, status, hint) and the queen. Measured again whenever a bee
  // picks a cell, because the copy can move without changing size: on a right-to-left page
  // the language sets dir="rtl" after this field has mounted and the copy jumps to the right.
  // False while the copy is not laid out: then no bee picks anything.
  const markCopy = () => {
    const copy = copyRef.current;
    let box = null;
    if (copy) {
      const h = host.getBoundingClientRect();
      const rects = [...copy.children, homeRef.current].filter(Boolean).map((k) => k.getBoundingClientRect());
      box = unionBox(rects, { x: h.left, y: h.top });
      if (!box) return false;
    }
    markFree(S.cells, { W: S.W, H: S.H, R: S.R, box });
    return true;
  };
  // Lays the honeycomb out again only when the field really changed size. Bees at work
  // keep working: with the same cell size what was in a cell moves to the nearest new one.
  const layout = () => {
    const W = host.clientWidth;
    const H = host.clientHeight;
    if (!W || !H || (W === S.W && H === S.H)) return false;
    const R = cellRadius(W);
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cells = layoutCells(W, H, R);
    if (R === S.R) {
      const moved = new Map();
      S.cells.forEach((c) => {
        if (c.state === 'empty') return;
        const n = nearestCell(cells, c.x, c.y);
        Object.assign(n, { state: c.state, p: c.p, born: c.born });
        moved.set(c, n);
      });
      S.jobs.forEach((j) => {
        j.cell = moved.get(j.cell) || nearestCell(cells, j.cell.x, j.cell.y);
        if (j.phase === 'out') j.path.to = perch(j.cell);
      });
    } else {
      // Another cell size (a phone turned, a window made narrow): the hive starts over.
      S.jobs = [];
      S.counts.work = 0;
      S.counts.needs = 0;
      S.seeded = false;
      setViews([]);
      emit();
    }
    Object.assign(S, { W, H, R, cells });
    setFieldWidth(W);
    markCopy();
    if (S.jobs.length) {
      const places = new Map(S.jobs.map((j) => [j.id, pillPlace(j.cell)]));
      setViews((vs) => vs.map((v) => (places.has(v.id) ? { ...v, ...places.get(v.id) } : v)));
      if (still) S.jobs.forEach((j) => { const p = perch(j.cell); put(j, p.x, p.y); });
    }
    return true;
  };

  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
    if (!layout()) markCopy();
    if (still) requestDraw();
  }) : null;
  ro?.observe(host);
  if (copyRef.current) ro?.observe(copyRef.current);
  const io = typeof IntersectionObserver === 'function' ? new IntersectionObserver(([entry]) => {
    S.onScreen = entry.isIntersecting;
    sync();
  }) : null;
  io?.observe(host);
  const onVisibility = () => {
    S.hidden = document.hidden;
    sync();
  };
  document.addEventListener('visibilitychange', onVisibility);

  setViews([]);
  emit();
  layout();
  sync();

  return {
    /** A bee's element came or went: it is placed where its job is now. */
    bind(id, el) {
      if (!el) return undefined;
      S.bees.set(id, el);
      const j = S.jobs.find((x) => x.id === id);
      if (j) put(j, j.pos.x, j.pos.y, j.pos.tilt);
      return () => {
        if (S.bees.get(id) === el) S.bees.delete(id);
      };
    },
    /** The pointer is at (clientX, clientY), or left the field (null). */
    point(cx, cy) {
      if (cx == null) {
        S.mouse = { x: -1e4, y: -1e4 };
      } else {
        const h = host.getBoundingClientRect();
        S.mouse = { x: cx - h.left, y: cy - h.top };
      }
      if (still) requestDraw();
    },
    /** A click or tap on the field: an empty cell gets a task, an orange one is approved. */
    click(cx, cy) {
      const h = host.getBoundingClientRect();
      const x = cx - h.left;
      const y = cy - h.top;
      const c = nearestCell(S.cells, x, y);
      if (!c || Math.hypot(c.x - x, c.y - y) > S.R) return;
      if (c.state === 'needs') {
        approve(S.jobs.find((j) => j.cell === c && j.phase === 'needs'));
        return;
      }
      if (c.state !== 'empty') return;
      if (!still) {
        if (S.jobs.length < MAX_JOBS) begin(c);
        return;
      }
      if (S.jobs.length >= STILL_MAX) {
        const oldest = S.jobs.find((j) => j.phase === 'done');
        if (!oldest) return;
        oldest.cell.state = 'empty';
        drop(oldest);
      }
      placeStill(c);
      emit();
      requestDraw();
    },
    approve(id) {
      approve(S.jobs.find((j) => j.id === id));
    },
    /** The pointer or the focus is on a waiting pill: it does not approve itself meanwhile. */
    hold(id, on) {
      const j = S.jobs.find((x) => x.id === id);
      if (j) j.held = on;
    },
    destroy() {
      S.dead = true;
      if (S.raf) cancelAnimationFrame(S.raf);
      S.raf = 0;
      ro?.disconnect();
      io?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

/** One bee; the engine moves its element every frame. */
const HiveBee = ({ id, look, state, bind }) => {
  const ref = useRef(null);
  useLayoutEffect(() => bind(id, ref.current), [id, bind]);
  return (
    <div ref={ref} className="hive-bee">
      <Bee species={look.species} color={look.color} accessory={look.accessory} state={state} size={54} />
    </div>
  );
};

/** A job's one-line status under its cell; waiting for an OK it is a button that approves. */
const HivePill = ({ view, text, approveLabel, fieldWidth, onApprove, onHold }) => {
  const ref = useRef(null);
  const asks = view.pill === 'needs';
  // Centered on its cell, but never past the field's edges (long words, narrow phones).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    el.style.left = `${clamp(view.x - w / 2, 12, Math.max(12, fieldWidth - 12 - w))}px`;
  }, [view.x, fieldWidth, text, asks]);
  const tone = asks ? 'needs' : view.pill === 'work' ? 'work' : 'done';
  const className = `hive-pill hive-pill--${tone}${view.shown ? ' is-shown' : ''}${view.above ? ' is-above' : ''}`;
  const style = { top: `${view.y}px` };
  const body = (
    <>
      <span className="hive-pill-dot" aria-hidden="true" />
      <span>
        {text}
        {asks && <>{' '}<b>{approveLabel}</b></>}
      </span>
    </>
  );
  if (!asks) return <div ref={ref} className={className} style={style}>{body}</div>;
  return (
    <button
      ref={ref}
      type="button"
      className={className}
      style={style}
      onClick={() => onApprove(view.id)}
      onPointerEnter={() => onHold(view.id, true)}
      onPointerLeave={() => onHold(view.id, false)}
      onFocus={() => onHold(view.id, true)}
      onBlur={() => onHold(view.id, false)}
    >
      {body}
    </button>
  );
};

/**
 * jobs: [{ look, asks, doing, result }] in the order bees take them. copyRef: the copy's
 * column (its children are the words bees plan around). homeRef: the queen the bees fly
 * from and back to. onCounts({ work, done, needs }) when a count changes (done adds up).
 */
export default function HiveField({ jobs, copyRef, homeRef, onCounts, approveLabel, approvedLabel, className = '', children }) {
  const still = useMediaQuery('(prefers-reduced-motion: reduce)', false);
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const hive = useRef(null);
  const latest = useRef({ jobs, onCounts });
  const [views, setViews] = useState([]);
  const [fieldWidth, setFieldWidth] = useState(0);

  useLayoutEffect(() => {
    latest.current = { jobs, onCounts };
  });

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas && typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null;
    if (!host || !ctx) return undefined;
    const engine = createHive({ host, canvas, ctx, still, latest, copyRef, homeRef, setViews, setFieldWidth });
    hive.current = engine;
    return () => {
      engine.destroy();
      if (hive.current === engine) hive.current = null;
    };
  }, [still, copyRef, homeRef]);

  const bind = useCallback((id, el) => hive.current?.bind(id, el), []);
  const approve = useCallback((id) => hive.current?.approve(id), []);
  const hold = useCallback((id, on) => hive.current?.hold(id, on), []);
  const point = (e) => hive.current?.point(e.clientX, e.clientY);
  const leave = () => hive.current?.point(null);
  // Links and buttons (the copy's, the pills') do their own thing; anywhere else is a cell.
  const click = (e) => {
    if (!e.target.closest('a, button')) hive.current?.click(e.clientX, e.clientY);
  };
  const pillText = (v) => {
    if (v.pill === 'approved') return approvedLabel;
    const job = jobs[v.spec];
    if (!job) return '';
    return v.pill === 'work' ? job.doing : job.result;
  };

  return (
    <div
      ref={hostRef}
      className={`hive-field ${className}`}
      onPointerMove={point}
      onPointerDown={point}
      onPointerLeave={leave}
      onPointerCancel={leave}
      onClick={click}
    >
      <canvas ref={canvasRef} className="hive-canvas" aria-hidden="true" />
      <div className="hive-copy">{children}</div>
      <div className="hive-layer hive-pills">
        {views.map((v) => (
          <HivePill key={v.id} view={v} text={pillText(v)} approveLabel={approveLabel} fieldWidth={fieldWidth} onApprove={approve} onHold={hold} />
        ))}
      </div>
      <div className="hive-layer hive-bees" aria-hidden="true">
        {views.map((v) => (v.bee && jobs[v.spec]
          ? <HiveBee key={v.id} id={v.id} look={jobs[v.spec].look} state={v.bee} bind={bind} />
          : null))}
      </div>
    </div>
  );
}
